import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { snapshot, type SnapshotContentItem } from 'services/api';

import { readSnapshotApiError, SnapshotEditorShell } from './SnapshotEditorShell';
import { snapshotItemUnlocked } from './SnapshotLock';

type Props = {
  item: SnapshotContentItem | null;
  initialChallengeIndex?: number | null;
  onOpenChange: (open: boolean) => void;
  onSaved: (item: SnapshotContentItem) => void;
};

type ChallengeDraft = { challenge: string; plan: string; raw: string };
type ContentDraft = { text: string; list: string[]; challenges: ChallengeDraft[] };

const stripLabel = (value: string, label: string) => value.replace(new RegExp(`^\\s*${label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:\\s*`, 'i'), '').trim();

export const SnapshotContentEditor = ({ item, initialChallengeIndex, onOpenChange, onSaved }: Props) => {
  const initial = useMemo<ContentDraft>(() => {
    if (!item) return { text: '', list: [], challenges: [] };
    if (item.kind === 'challenges' && Array.isArray(item.value)) {
      const entries = item.display_value as ChallengeDraft[];
      return { text: '', list: [], challenges: item.value.map((raw, index) => ({
        challenge: entries[index]?.challenge ?? raw,
        plan: entries[index]?.plan ?? '',
        raw,
      })) };
    }
    if (Array.isArray(item.value)) {
      const displayed = item.kind === 'goal_source' ? item.display_value as { label: string; value: string }[] : [];
      const list = item.labels?.length
        ? item.labels.map((label, index) => displayed[index]?.label === label
          ? displayed[index].value
          : stripLabel(item.value[index] || '', label))
        : item.value;
      return { text: '', list, challenges: [] };
    }
    return { text: item.value, list: [], challenges: [] };
  }, [item]);
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setDraft(initial); setSaving(false); setError(''); }, [initial]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const isList = Boolean(item && Array.isArray(item.value));
  const isChallenges = item?.kind === 'challenges';
  const listValues = isChallenges ? draft.challenges.map((entry) => entry.challenge) : draft.list;

  if (!item || !snapshotItemUnlocked(item)) return null;

  const update = (index: number, value: string) => setDraft((current) => isChallenges
    ? { ...current, challenges: current.challenges.map((entry, entryIndex) => entryIndex === index ? { ...entry, challenge: value } : entry) }
    : { ...current, list: current.list.map((entry, entryIndex) => entryIndex === index ? value : entry) });
  const move = (index: number, offset: -1 | 1) => setDraft((current) => {
    const target = index + offset;
    if (isChallenges) {
      if (target < 0 || target >= current.challenges.length) return current;
      const challenges = [...current.challenges];
      [challenges[index], challenges[target]] = [challenges[target], challenges[index]];
      return { ...current, challenges };
    }
    if (target < 0 || target >= current.list.length) return current;
    const list = [...current.list]; [list[index], list[target]] = [list[target], list[index]];
    return { ...current, list };
  });

  const save = async () => {
    if (isChallenges && draft.challenges.some((entry) => entry.plan.trim() && !entry.challenge.trim())) {
      setError('Add a challenge for each response plan.');
      return;
    }
    setSaving(true); setError('');
    try {
      const value = isChallenges
        ? draft.challenges.map((entry) => {
          const original = initial.challenges.find((candidate) => candidate.raw === entry.raw);
          if (original && original.challenge === entry.challenge && original.plan === entry.plan) return entry.raw;
          if (!entry.challenge.trim() && !entry.plan.trim()) return '';
          return `Challenge: ${entry.challenge.trim()}\nPlan: ${entry.plan.trim()}`.trim();
        })
        : isList
        ? item.labels?.length
          ? item.labels.map((label, index) => `${label}: ${draft.list[index] || ''}`.trim())
          : draft.list
        : draft.text;
      if (isChallenges && Array.isArray(value) && value.some((entry) => Array.from(entry).length > 4000)) {
        setError('This challenge and response plan are too long to save. Shorten them and try again.');
        return;
      }
      const saved = await snapshot.saveContent(item.key, value);
      onSaved(saved);
      onOpenChange(false);
    } catch (saveError) {
      setError(readSnapshotApiError(saveError).message);
    } finally { setSaving(false); }
  };

  const question = item.question_text || item.subtext || 'Write what feels true for you right now.';
  const canAdd = !item.labels?.length && listValues.length < (item.max_items || 20);
  const hasContent = isList ? listValues.some((value) => value.trim()) : draft.text.trim();

  return (
    <SnapshotEditorShell open title={item.title} description={`“${question}”`} dirty={dirty && !saving} onOpenChange={onOpenChange}>
      <div className="space-y-5">
        {isList ? (
          <div className="space-y-3">
            {listValues.map((value, index) => (
              <div key={`${item.key}-${index}`} className="rounded-2xl bg-foundation-calm-blue-50 p-3">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor={`snapshot-${item.key}-${index}`} className="min-w-0 [overflow-wrap:anywhere]">{item.labels?.[index] || `${item.kind === 'challenges' ? 'Challenge' : 'Item'} ${index + 1}`}</Label>
                  {!item.labels?.length ? (
                    <div className="flex shrink-0">
                      <button type="button" className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white focus-visible:ring-2" disabled={index === 0} onClick={() => move(index, -1)} aria-label={`Move item ${index + 1} up`}><ChevronUp className="h-4 w-4" /></button>
                      <button type="button" className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white focus-visible:ring-2" disabled={index === listValues.length - 1} onClick={() => move(index, 1)} aria-label={`Move item ${index + 1} down`}><ChevronDown className="h-4 w-4" /></button>
                      <button type="button" className="flex h-11 w-11 items-center justify-center rounded-full text-red-700 hover:bg-red-50 focus-visible:ring-2" onClick={() => setDraft((current) => isChallenges
                        ? { ...current, challenges: current.challenges.filter((_, entryIndex) => entryIndex !== index) }
                        : { ...current, list: current.list.filter((_, entryIndex) => entryIndex !== index) })} aria-label={`Remove item ${index + 1}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ) : null}
                </div>
                <Textarea id={`snapshot-${item.key}-${index}`} className="mt-2 min-h-20 bg-white" value={value} onChange={(event) => update(index, event.target.value)} />
                {isChallenges ? <div className="mt-3">
                  <Label htmlFor={`snapshot-${item.key}-${index}-plan`}>Response plan {index + 1} (optional)</Label>
                  <Textarea id={`snapshot-${item.key}-${index}-plan`} autoFocus={initialChallengeIndex === index} className="mt-2 min-h-20 bg-white" placeholder="How are you responding, or what could you try?" value={draft.challenges[index].plan} onChange={(event) => {
                    const plan = event.target.value;
                    setDraft((current) => ({ ...current, challenges: current.challenges.map((entry, entryIndex) => entryIndex === index ? { ...entry, plan } : entry) }));
                  }} />
                </div> : null}
              </div>
            ))}
            {canAdd ? <Button type="button" variant="secondary" size="auto" className="min-h-11" onClick={() => setDraft((current) => isChallenges
              ? { ...current, challenges: [...current.challenges, { challenge: '', plan: '', raw: '' }] }
              : { ...current, list: [...current.list, ''] })}><Plus className="mr-1 h-4 w-4" /> {isChallenges ? 'Add challenge' : 'Add item'}</Button> : null}
          </div>
        ) : item.kind === 'short_text' ? (
          <Input autoFocus className="min-h-11" value={draft.text} onChange={(event) => setDraft({ ...draft, text: event.target.value })} />
        ) : (
          <Textarea autoFocus className="min-h-44" value={draft.text} onChange={(event) => setDraft({ ...draft, text: event.target.value })} />
        )}
        {error ? <p role="alert" className="rounded-xl bg-foundation-orange-50 px-3 py-2 text-sm text-foundation-orange-900">{error}</p> : null}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" size="auto" className="min-h-11" disabled={saving} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" size="auto" className="min-h-11" disabled={saving || !dirty || !hasContent} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>
    </SnapshotEditorShell>
  );
};
